import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const DEFAULT_ROTATE_INTERVAL_MS = 10000;

const BannerCarousel = ({ banners, intervalMs = DEFAULT_ROTATE_INTERVAL_MS }) => {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  // Natural width/height ratio of each banner's uploaded image, keyed by banner id
  const [ratios, setRatios] = useState({});

  // Reset to the first slide whenever the banner set changes
  useEffect(() => {
    setIndex(0);
  }, [banners]);

  const handleImageLoad = useCallback((bannerId, e) => {
    const { naturalWidth, naturalHeight } = e.target;
    if (!naturalWidth || !naturalHeight) return;
    setRatios(prev => (
      prev[bannerId] === naturalWidth / naturalHeight ? prev : { ...prev, [bannerId]: naturalWidth / naturalHeight }
    ));
  }, []);

  useEffect(() => {
    if (banners.length < 2) return undefined;
    const timer = setInterval(() => {
      setIndex(i => (i + 1) % banners.length);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [banners.length, intervalMs]);

  const handleClick = useCallback((banner) => {
    const link = banner.link?.trim();
    if (!link) return;
    if (/^https?:\/\//i.test(link)) {
      window.location.href = link;
    } else {
      navigate(link.startsWith('/') ? link : `/${link}`);
    }
  }, [navigate]);

  if (banners.length === 0) return null;

  const activeRatio = ratios[banners[index]?.id];

  return (
    <div
      className={`relative w-full overflow-hidden bg-gray-100 ${activeRatio ? '' : 'h-[30vh] md:h-screen'}`}
      style={activeRatio ? { aspectRatio: activeRatio } : undefined}
    >
      {banners.map((banner, i) => (
        <div
          key={banner.id}
          onClick={() => handleClick(banner)}
          className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
            i === index ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
          } ${banner.link ? 'cursor-pointer' : ''}`}
        >
          <img
            src={banner.image}
            srcSet={banner.image_srcset || undefined}
            sizes="100vw"
            alt={banner.name}
            className="w-full h-full object-cover"
            onLoad={(e) => handleImageLoad(banner.id, e)}
            loading={i === 0 ? 'eager' : 'lazy'}
            fetchPriority={i === 0 ? 'high' : 'auto'}
            decoding={i === 0 ? 'sync' : 'async'}
          />
        </div>
      ))}

      {banners.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-20">
          {banners.map((banner, i) => (
            <button
              key={banner.id}
              onClick={() => setIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`w-2 h-2 rounded-full transition-colors ${i === index ? 'bg-white' : 'bg-white/50 hover:bg-white/80'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default BannerCarousel;
